/* eslint-disable max-lines */
import {
  Alert,
  Box,
  Button,
  Center,
  Divider,
  Group,
  Loader,
  Modal,
  Stack,
  Stepper,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import {
  BookOpen,
  MagnifyingGlass,
  WarningCircle,
  X,
} from '@phosphor-icons/react';
import sortBy from 'lodash/sortBy';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';

import {
  SCHEDULE_FREQUENCY,
  FREQUENCY_DAYS,
} from 'api/types/card-schedule.types';
import { permissionIds } from 'components/common/klueCard/partials/klue-card-visibility-groups/helpers';
import PermissionsStep from 'components/common/klueCard/partials/wizard-steps/PermissionsStep';
import RivalRow from 'components/common/klueCard/partials/wizard-steps/RivalRow';
import ScheduleStep from 'components/common/klueCard/partials/wizard-steps/ScheduleStep';
import { useNotifier, NotifierType } from 'hooks/use-notifier';
import useVisibilityGroups from 'hooks/use-visibility-groups/useVisibilityGroups';
import store from 'store';
import { useCreateCardScheduleMutation } from 'store/api/card-schedules/card-schedules.api';
import { useCreateCardMutation } from 'store/api/cards';
import { useGetUserByIdQuery } from 'store/api/users/users.api';
import TRACKING_IDS from 'tracking-ids';

import {
  PROMPTS,
  substituteVariables,
  type PromptType,
} from './hooks/usePromptLibrary';

import type { CompanyType } from 'api/api.types';
import type { ScheduleFrequency } from 'api/types/card-schedule.types';
import type { RivalSelection } from 'components/common/klueCard/partials/wizard-steps/RivalRow';
import type { ChangeEvent, ReactNode } from 'react';
import type { Dispatch, RootState } from 'store/store.types';

type AdvancedPromptWizardProps = {
  onClose: () => void;
};

const selectRivals = store.select(({ rivals: { all } }) => ({
  rivals: all,
}));

const RIVAL_ROW_TRACKING_IDS = {
  boardSelect: TRACKING_IDS.askKlue.advancedWizard.boardSelect,
  laneSelect: TRACKING_IDS.askKlue.advancedWizard.laneSelect,
  addLane: TRACKING_IDS.askKlue.advancedWizard.addLane,
};

// ─── Step 1: Prompt Selection ───────────────────────────────────────────────

const TYPE_ORDER = ['battlecards', 'seller', 'pmm', 'executive', 'simulation'];

const prompts: PromptType[] = PROMPTS.map((tpl) => ({
  id: tpl.id,
  type: tpl.type,
  simple: tpl.simple,
  prompt: tpl.prompt,
  simpleTemplate: tpl.simple,
  promptTemplate: tpl.prompt,
}));

const groupedPrompts: Record<string, PromptType[]> = {};
prompts.forEach((p) => {
  if (!groupedPrompts[p.type]) groupedPrompts[p.type] = [];
  groupedPrompts[p.type].push(p);
});

const availableTypes = TYPE_ORDER.filter(
  (type) => groupedPrompts[type]?.length > 0,
);

function PromptGroupTabs({
  currentTab,
  onSelectTab,
}: {
  currentTab: string;
  onSelectTab: (tab: string) => void;
}): ReactNode {
  const { t } = useTranslation(['Dashboard']);

  if (availableTypes.length <= 1) return null;

  return (
    <Group gap="lg" justify="flex-start">
      {availableTypes.map((type) => (
        <Box
          key={type}
          onClick={() => onSelectTab(type)}
          style={{ cursor: 'pointer', position: 'relative' }}
        >
          <Text
            size="sm"
            fw={currentTab === type ? '600' : '400'}
            c={currentTab === type ? 'dark' : 'dimmed'}
          >
            {t(`Dashboard:askKlue.promptLibrary.types.${type}`)}
          </Text>
          {currentTab === type && (
            <Box
              style={{
                position: 'absolute',
                bottom: -4,
                left: 0,
                right: 0,
                height: 3,
                backgroundColor: 'var(--mantine-color-blue-6)',
                borderRadius: 2,
              }}
            />
          )}
        </Box>
      ))}
    </Group>
  );
}

function PromptGroup({
  currentTab,
  selectedPromptId,
  onSelectPrompt,
  competitorLabel,
  companyName,
}: {
  currentTab: string;
  selectedPromptId: number | null;
  onSelectPrompt: (prompt: PromptType) => void;
  competitorLabel: string;
  companyName: string;
}): ReactNode {
  return (
    <Stack gap={0}>
      {currentTab &&
        groupedPrompts[currentTab]?.map((prompt) => (
          <Box
            key={prompt.id}
            onClick={() => onSelectPrompt(prompt)}
            p="sm"
            style={{
              cursor: 'pointer',
              borderRadius: 4,
              backgroundColor:
                selectedPromptId === prompt.id
                  ? 'var(--mantine-color-blue-0)'
                  : undefined,
            }}
          >
            <Text
              size="sm"
              c={
                selectedPromptId === prompt.id
                  ? 'var(--mantine-color-blue-7)'
                  : undefined
              }
              fw={selectedPromptId === prompt.id ? 600 : 400}
            >
              {prompt.simple
                .replace(/\$\{rival\}/g, competitorLabel)
                .replace(/\$\{company\}/g, companyName)}
            </Text>
          </Box>
        ))}
    </Stack>
  );
}

function PromptStep({
  company,
  currentTab,
  selectedPromptId,
  onSelect,
}: {
  company: CompanyType | null;
  currentTab: string;
  selectedPromptId: number | null;
  onSelect: (prompt: PromptType) => void;
}): ReactNode {
  const { t } = useTranslation(['Dashboard']);
  const competitorLabel = t(
    'Dashboard:askKlue.promptLibrary.advancedWizard.theCompetitor',
  );

  return (
    <Stack gap="md">
      <PromptGroup
        currentTab={currentTab}
        selectedPromptId={selectedPromptId}
        onSelectPrompt={onSelect}
        competitorLabel={competitorLabel}
        companyName={company?.name ?? ''}
      />
    </Stack>
  );
}

// ─── Step 5: Competitor + Lane Selection ────────────────────────────────────

function RivalStep({
  selections,
  onToggle,
  onBoardChange,
  isPromptRivalSpecific,
  isUnscheduled,
  rivalAlertDismissed,
  scheduleAlertDismissed,
  onDismissRivalAlert,
  onDismissScheduleAlert,
}: {
  selections: Record<number, RivalSelection>;
  onToggle: (rivalId: number) => void;
  onBoardChange: (rivalId: number, boardId: number | undefined) => void;
  isPromptRivalSpecific: boolean;
  isUnscheduled: boolean;
  rivalAlertDismissed: boolean;
  scheduleAlertDismissed: boolean;
  onDismissRivalAlert: () => void;
  onDismissScheduleAlert: () => void;
}): ReactNode {
  const { t } = useTranslation(['Dashboard', 'Card']);
  const { rivals } = useSelector(selectRivals);
  const [search, setSearch] = useState('');

  // Defer rival list rendering to avoid blocking the step transition
  const [isReady, setIsReady] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setIsReady(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const sortedRivals = useMemo(
    () => sortBy(rivals, [(r) => r.name.toLowerCase()]),
    [rivals],
  );

  const filteredRivals = useMemo(() => {
    if (!search.trim()) return sortedRivals;
    const lower = search.toLowerCase();
    return sortedRivals.filter((r) => r.name.toLowerCase().includes(lower));
  }, [sortedRivals, search]);

  return (
    <Stack gap="md">
      <TextInput
        placeholder={t(
          'Dashboard:askKlue.promptLibrary.advancedWizard.searchRivals',
        )}
        leftSection={<MagnifyingGlass size={16} />}
        value={search}
        onChange={(e) => setSearch(e.currentTarget.value)}
      />

      {!rivalAlertDismissed && !isPromptRivalSpecific && (
        <Alert
          icon={<WarningCircle size={20} />}
          color="red"
          withCloseButton
          onClose={onDismissRivalAlert}
          title={t(
            'Dashboard:askKlue.promptLibrary.advancedWizard.notRivalSpecific',
          )}
        />
      )}

      {!scheduleAlertDismissed && isUnscheduled && (
        <Alert
          icon={<WarningCircle size={20} />}
          color="yellow"
          withCloseButton
          onClose={onDismissScheduleAlert}
          title={t(
            'Dashboard:askKlue.promptLibrary.advancedWizard.unscheduledWarning',
          )}
        />
      )}

      {!isReady ? (
        <Center p="lg">
          <Loader size="sm" />
        </Center>
      ) : (
        <Stack gap={0}>
          {filteredRivals.map((rival, index) => (
            <Stack key={rival.id} gap={0}>
              {index > 0 && <Divider />}
              <Stack gap={4} py="sm">
                <RivalRow
                  rival={rival}
                  selection={selections[rival.id] ?? { checked: false }}
                  onToggle={() => onToggle(rival.id)}
                  onBoardChange={(boardId) => onBoardChange(rival.id, boardId)}
                  trackingIds={RIVAL_ROW_TRACKING_IDS}
                />
              </Stack>
            </Stack>
          ))}
        </Stack>
      )}
    </Stack>
  );
}

// ─── Main Wizard ────────────────────────────────────────────────────────────

export default function AdvancedPromptWizard({
  onClose,
}: AdvancedPromptWizardProps): ReactNode {
  const { t } = useTranslation(['Dashboard', 'Card', 'Common']);
  const { notify } = useNotifier();
  const dispatch = useDispatch<Dispatch>();
  const { rivals } = useSelector(selectRivals);
  const [activeTab, setActiveTab] = useState('');

  useEffect(() => {
    if (availableTypes.length > 0 && !activeTab) {
      setActiveTab(availableTypes[0]);
    }
  }, [activeTab]);

  const currentTab = activeTab || availableTypes[0] || '';

  const company = useSelector<RootState, CompanyType | null>(
    (state) => state.auth.company,
  );

  const { visibleTo, selectedGroups, deselectAllGroups, setVisibleTo } =
    useVisibilityGroups();

  // Fetch visibility groups and reset on mount
  useEffect(() => {
    deselectAllGroups();
    setVisibleTo(permissionIds.curatorsOnly);
    dispatch.visibilityGroups.fetchVisibilityGroupsFromApi();
  }, [deselectAllGroups, setVisibleTo, dispatch.visibilityGroups]);

  // Defer heavy content rendering to avoid blocking the modal open animation
  const [isReady, setIsReady] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setIsReady(true));
    return () => cancelAnimationFrame(id);
  }, []);

  // Wizard state
  const [step, setStep] = useState(0);
  const [selectedPrompt, setSelectedPrompt] = useState<PromptType | null>(null);
  const [editedPromptTemplate, setEditedPromptTemplate] = useState('');
  const [frequency, setFrequency] = useState<ScheduleFrequency>(
    SCHEDULE_FREQUENCY.MONTHLY,
  );
  const [selections, setSelections] = useState<Record<number, RivalSelection>>(
    {},
  );
  const [isApplying, setIsApplying] = useState(false);

  // Alert dismissal state (lifted here so outer ScrollArea can dismiss on scroll)
  const [rivalAlertDismissed, setRivalAlertDismissed] = useState(false);
  const [scheduleAlertDismissed, setScheduleAlertDismissed] = useState(false);

  useEffect(() => {
    if (step === 4) {
      setRivalAlertDismissed(false);
      setScheduleAlertDismissed(false);
    }
  }, [step]);

  const handleSelectPrompt = useCallback((prompt: PromptType) => {
    setSelectedPrompt(prompt);
    setEditedPromptTemplate(prompt.promptTemplate);
  }, []);

  const [createCard] = useCreateCardMutation();
  const [createCardSchedule] = useCreateCardScheduleMutation();
  const { data: klueAIUser } = useGetUserByIdQuery('klue_ai');

  const toggleRival = useCallback((rivalId: number) => {
    setSelections((prev) => {
      const current = prev[rivalId] ?? { checked: false };
      return {
        ...prev,
        [rivalId]: {
          ...current,
          checked: !current.checked,
          boardId: !current.checked ? current.boardId : undefined,
        },
      };
    });
  }, []);

  const updateBoardId = useCallback(
    (rivalId: number, boardId: number | undefined) => {
      setSelections((prev) => ({
        ...prev,
        [rivalId]: { ...prev[rivalId], checked: true, boardId },
      }));
    },
    [],
  );

  const validTargets = useMemo(
    () =>
      Object.entries(selections)
        .filter(
          (entry): entry is [string, RivalSelection & { boardId: number }] =>
            entry[1].checked &&
            entry[1].boardId !== undefined &&
            entry[1].status !== 'success',
        )
        .map(([rivalId, sel]) => ({
          rivalId: Number(rivalId),
          boardId: sel.boardId,
          rivalName: rivals.find((r) => r.id === Number(rivalId))?.name ?? '',
          existingCardId: sel.cardId,
        })),
    [selections, rivals],
  );

  const checkedCount = useMemo(
    () =>
      Object.values(selections).filter(
        (s) => s.checked && s.status !== 'success',
      ).length,
    [selections],
  );

  const isLastStepValid =
    validTargets.length > 0 &&
    validTargets.length === checkedCount &&
    !!klueAIUser;

  const isPromptRivalSpecific = editedPromptTemplate
    ? editedPromptTemplate.includes('${rival}')
    : true;

  const isUnscheduled = frequency === SCHEDULE_FREQUENCY.UNSCHEDULED;

  const handleApply = useCallback(async () => {
    if (!selectedPrompt || !isLastStepValid) return;

    setIsApplying(true);

    const frequencyDays = isUnscheduled
      ? null
      : FREQUENCY_DAYS[frequency as Exclude<ScheduleFrequency, 'unscheduled'>];

    const isDraft = visibleTo === permissionIds.curatorsOnly;
    const allAccess = visibleTo === permissionIds.everyone;

    const results = await Promise.allSettled(
      validTargets.map(async (target) => {
        const promptText = substituteVariables(editedPromptTemplate, company, {
          name: target.rivalName,
        });

        let cardId = target.existingCardId;

        if (!cardId) {
          const cardTitle = substituteVariables(
            selectedPrompt.simpleTemplate,
            company,
            { name: target.rivalName },
          );

          const newCard = await createCard({
            data: { name: cardTitle },
            viewOrder: 0,
            boardId: target.boardId,
            author: klueAIUser?.id ?? 0,
            templateName: 'CardHTML',
            isDraft,
            allAccess: !isDraft && allAccess,
            visibilityGroups:
              isDraft || allAccess ? undefined : Array.from(selectedGroups),
          }).unwrap();

          cardId = newCard?.id;
        }

        if (cardId) {
          try {
            await createCardSchedule({
              cardId,
              payload: {
                prompt: promptText,
                researchMode: true,
                disabledSourceTypes: [],
                frequencyDays,
              },
            }).unwrap();
          } catch (error) {
            throw { cardId, cause: error };
          }
        }
      }),
    );

    const failures: { rivalId: number; cardId?: number }[] = [];
    let succeededCount = 0;

    results.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        succeededCount++;
      } else {
        const reason = result.reason as { cardId?: number } | undefined;
        failures.push({
          rivalId: validTargets[index].rivalId,
          cardId: reason?.cardId,
        });
      }
    });

    if (succeededCount > 0) {
      notify({
        type: NotifierType.SUCCESS,
        message: t('Dashboard:askKlue.promptLibrary.advancedWizard.success', {
          count: succeededCount,
        }),
      });
    }
    if (failures.length > 0) {
      notify({
        type: NotifierType.ERROR,
        message: t('Dashboard:askKlue.promptLibrary.advancedWizard.error', {
          count: failures.length,
        }),
      });
      const failedMap = new Map(failures.map((f) => [f.rivalId, f.cardId]));
      setSelections((prev) => {
        const next = { ...prev };
        for (const [id, sel] of Object.entries(next)) {
          if (!sel.checked) continue;
          const numId = Number(id);
          next[numId] = {
            ...sel,
            status: failedMap.has(numId) ? 'error' : 'success',
            cardId: failedMap.get(numId) ?? sel.cardId,
          };
        }
        return next;
      });
    } else {
      onClose();
    }
    setIsApplying(false);
  }, [
    selectedPrompt,
    editedPromptTemplate,
    isLastStepValid,
    isUnscheduled,
    frequency,
    validTargets,
    company,
    visibleTo,
    selectedGroups,
    createCard,
    createCardSchedule,
    klueAIUser,
    notify,
    t,
    onClose,
  ]);

  const canGoNext = step === 0 ? !!selectedPrompt : true;

  const stepContentLabel = useMemo(() => {
    let label = '';

    switch (step) {
      case 0:
        return (
          <PromptGroupTabs currentTab={currentTab} onSelectTab={setActiveTab} />
        );
      case 1:
        label = t(
          'Dashboard:askKlue.promptLibrary.advancedWizard.editPromptLabel',
        );
        break;
      case 2:
        label = t('Dashboard:askKlue.createCardModal.visibilityLabel');
        break;
      case 3:
        label = t('Dashboard:askKlue.promptLibrary.advancedWizard.frequency');
        break;
      case 4:
        label = t(
          'Dashboard:askKlue.promptLibrary.advancedWizard.competitorsLabel',
        );
        break;
      default:
        label = '';
    }

    return (
      <Text fw={600} fz="lg">
        {label}
      </Text>
    );
  }, [step, currentTab, t]);

  const stepContent = useMemo(() => {
    switch (step) {
      case 0:
        return (
          <PromptStep
            company={company}
            currentTab={currentTab}
            selectedPromptId={selectedPrompt?.id ?? null}
            onSelect={handleSelectPrompt}
          />
        );
      case 1:
        return (
          <Textarea
            value={editedPromptTemplate}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
              setEditedPromptTemplate(e.target.value)
            }
            styles={{
              root: { flex: 1, display: 'flex', flexDirection: 'column' },
              wrapper: { flex: 1 },
              input: { height: '100%' },
            }}
          />
        );
      case 2:
        return null;
      case 3:
        return (
          <ScheduleStep
            frequency={frequency}
            onFrequencyChange={setFrequency}
          />
        );
      case 4:
        return (
          <RivalStep
            selections={selections}
            onToggle={toggleRival}
            onBoardChange={updateBoardId}
            isPromptRivalSpecific={isPromptRivalSpecific}
            isUnscheduled={isUnscheduled}
            rivalAlertDismissed={rivalAlertDismissed}
            scheduleAlertDismissed={scheduleAlertDismissed}
            onDismissRivalAlert={() => setRivalAlertDismissed(true)}
            onDismissScheduleAlert={() => setScheduleAlertDismissed(true)}
          />
        );
      default:
        return null;
    }
  }, [
    step,
    currentTab,
    company,
    selectedPrompt?.id,
    handleSelectPrompt,
    editedPromptTemplate,
    frequency,
    selections,
    toggleRival,
    updateBoardId,
    isPromptRivalSpecific,
    isUnscheduled,
    rivalAlertDismissed,
    scheduleAlertDismissed,
  ]);

  return (
    <Modal
      opened
      onClose={onClose}
      closeOnClickOutside={!isApplying}
      closeOnEscape={!isApplying}
      size={780}
      centered
      title={
        <Group gap="sm" align="center">
          <BookOpen />
          <Title order={2} size="h4">
            {t('Dashboard:askKlue.promptLibrary.advancedWizard.title')}
          </Title>
        </Group>
      }
      closeButtonProps={{ icon: <X />, disabled: isApplying }}
      styles={{
        header: {
          backgroundColor: 'var(--tokens-color-primary-main)',
          color: 'white',
        },
        title: { color: 'white' },
        close: { color: 'white', backgroundColor: 'transparent' },
        content: {
          height: '90vh',
          maxHeight: 700,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        },
        body: {
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          minHeight: 0,
        },
      }}
    >
      {!isReady ? (
        <Center p="lg" style={{ minHeight: 520 }}>
          <Loader />
        </Center>
      ) : (
        <Stack h="100%">
          <Box p="md">
            <Stepper active={step} size="sm">
              <Stepper.Step
                label={t(
                  'Dashboard:askKlue.promptLibrary.advancedWizard.step1Title',
                )}
              />
              <Stepper.Step
                label={t(
                  'Dashboard:askKlue.promptLibrary.advancedWizard.step2Title',
                )}
              />
              <Stepper.Step
                label={t(
                  'Dashboard:askKlue.promptLibrary.advancedWizard.step3Title',
                )}
              />
              <Stepper.Step
                label={t(
                  'Dashboard:askKlue.promptLibrary.advancedWizard.step4Title',
                )}
              />
              <Stepper.Step
                label={t(
                  'Dashboard:askKlue.promptLibrary.advancedWizard.step5Title',
                )}
              />
            </Stepper>
          </Box>

          <Stack style={{ flex: 1, overflow: 'hidden', minHeight: 0 }} gap={0}>
            <Box p="md" pb={0}>
              {stepContentLabel}
            </Box>
            <Box
              style={{
                flex: 1,
                overflowY: step === 1 ? 'hidden' : 'auto',
                display: 'flex',
                flexDirection: 'column',
              }}
              p="md"
            >
              {stepContent}
              <Box display={step === 2 ? undefined : 'none'}>
                <PermissionsStep
                  visibleToDefault={permissionIds.curatorsOnly}
                />
              </Box>
            </Box>
          </Stack>

          <Box>
            <Group justify="space-between">
              <Button
                variant="subtle"
                color="gray"
                onClick={onClose}
                disabled={isApplying}
                data-tracking-id={TRACKING_IDS.askKlue.advancedWizard.cancel}
              >
                {t('Common:actions.cancel')}
              </Button>
              <Group gap="sm">
                {step > 0 && (
                  <Button
                    variant="subtle"
                    color="gray"
                    onClick={() => setStep((s) => s - 1)}
                    disabled={isApplying}
                  >
                    {t('Dashboard:askKlue.promptLibrary.advancedWizard.back')}
                  </Button>
                )}
                {step < 4 && (
                  <Button
                    variant="filled"
                    onClick={() => setStep((s) => s + 1)}
                    disabled={!canGoNext}
                  >
                    {t('Dashboard:askKlue.promptLibrary.advancedWizard.next')}
                  </Button>
                )}
                {step === 4 && (
                  <Button
                    variant="filled"
                    onClick={handleApply}
                    loading={isApplying}
                    disabled={!isLastStepValid}
                    data-tracking-id={TRACKING_IDS.askKlue.advancedWizard.apply}
                  >
                    {t('Dashboard:askKlue.promptLibrary.advancedWizard.apply')}
                  </Button>
                )}
              </Group>
            </Group>
          </Box>
        </Stack>
      )}
    </Modal>
  );
}
